var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { faPalette } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useFocusWithin } from "@vueuse/core";
import { BButton, BButtonGroup } from "bootstrap-vue";
import { sanitize } from "dompurify";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useWorkflowStores } from "@/composables/workflowStores";
import { colors } from "./colors";
import { useResizable } from "./useResizable";
import { selectAllText } from "./utilities";
import ColorSelector from "./ColorSelector.vue";
import DraggablePan from "@/components/Workflow/Editor/DraggablePan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'TextComment',
    props: {
        comment: null,
        rootOffset: null,
        scale: null,
        readonly: { type: Boolean }
    },
    emits: ["change", "resize", "move", "pan-by", "remove", "set-color"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faTrashAlt, faPalette);
        const resizeContainer = ref();
        useResizable(resizeContainer, computed(() => props.comment.size), ([width, height]) => {
            emit("resize", [width, height]);
            saveText();
        });
        function escapeAndSanitize(text) {
            return sanitize(text, { ALLOWED_TAGS: ["br"] }).replace(/(?:^(\s|&nbsp;)+)|(?:(\s|&nbsp;)+$)/g, "");
        }
        const editableElement = ref();
        function getInnerText() {
            var _a;
            const element = editableElement.value;
            if (element) {
                const value = (_a = element.innerHTML) !== null && _a !== void 0 ? _a : "";
                return escapeAndSanitize(value);
            }
            else {
                return "";
            }
        }
        function saveText() {
            emit("change", Object.assign(Object.assign({}, props.comment.data), { text: getInnerText() }));
        }
        function toggleBold() {
            if (props.comment.data.bold) {
                const _a = props.comment.data, { bold: _bold } = _a, data = __rest(_a, ["bold"]);
                emit("change", data);
            }
            else {
                emit("change", Object.assign(Object.assign({}, props.comment.data), { bold: true }));
            }
        }
        function toggleItalic() {
            if (props.comment.data.italic) {
                const _a = props.comment.data, { italic: _italic } = _a, data = __rest(_a, ["italic"]);
                emit("change", data);
            }
            else {
                emit("change", Object.assign(Object.assign({}, props.comment.data), { italic: true }));
            }
        }
        const fontSize = computed(() => props.comment.data.size);
        const canIncreaseFontSize = computed(() => fontSize.value < 5);
        const canDecreaseFontSize = computed(() => fontSize.value > 1);
        function increaseFontSize() {
            if (canIncreaseFontSize.value) {
                emit("change", Object.assign(Object.assign({}, props.comment.data), { size: fontSize.value + 1 }));
            }
        }
        const increaseFontSizeTitle = computed(() => canIncreaseFontSize.value ? `Increase font size to ${fontSize.value + 1}` : "Maximum font size");
        function decreaseFontSize() {
            if (canDecreaseFontSize.value) {
                emit("change", Object.assign(Object.assign({}, props.comment.data), { size: fontSize.value - 1 }));
            }
        }
        const decreaseFontSizeTitle = computed(() => canDecreaseFontSize.value ? `Decrease font size to ${fontSize.value - 1}` : "Minimum font size");
        function onRootClick() {
            var _a;
            (_a = editableElement.value) === null || _a === void 0 ? void 0 : _a.focus();
        }
        function onMove(position) {
            emit("move", [position.x, position.y]);
        }
        const showColorSelector = ref(false);
        const rootElement = ref();
        const { focused } = useFocusWithin(rootElement);
        watch(() => focused.value, () => {
            if (!focused.value) {
                showColorSelector.value = false;
                if (getInnerText() === "") {
                    emit("remove");
                }
                else {
                    saveText();
                }
            }
        });
        function onSetColor(color) {
            emit("set-color", color);
        }
        const cssVariables = computed(() => {
            const vars = {
                "--font-size": `${fontSize.value}rem`,
            };
            if (props.comment.color !== "none") {
                vars["--font-color"] = colors[props.comment.color];
            }
            return vars;
        });
        const { commentStore } = useWorkflowStores();
        function onDoubleClick() {
            if (editableElement.value) {
                selectAllText(editableElement.value);
            }
        }
        onMounted(() => {
            if (commentStore.isJustCreated(props.comment.id) && editableElement.value) {
                selectAllText(editableElement.value);
            }
        });
        return { __sfc: true, props, emit, resizeContainer, escapeAndSanitize, editableElement, getInnerText, saveText, toggleBold, toggleItalic, fontSize, canIncreaseFontSize, canDecreaseFontSize, increaseFontSize, increaseFontSizeTitle, decreaseFontSize, decreaseFontSizeTitle, onRootClick, onMove, showColorSelector, rootElement, focused, onSetColor, cssVariables, commentStore, onDoubleClick, FontAwesomeIcon, BButton, BButtonGroup, reactive, ColorSelector, DraggablePan };
    }
});
